/**
 * @baba/i18n · locale detection
 *
 * Resolution chain (highest-confidence first):
 *   1. Explicit user setting (localStorage / chrome.storage.sync)
 *   2. navigator.languages[*] — browser-declared preferences
 *   3. chrome.i18n.getUILanguage() — extension UI locale (extension only)
 *   4. geoip country → locale mapping (if the caller has it)
 *   5. Intl.DateTimeFormat timezone fallback (e.g. Asia/Jerusalem → he)
 *   6. DEFAULT_LOCALE ('en')
 *
 * Caller passes any signals they have. The function is pure; callers
 * handle the IO (reading storage, making geo requests, etc).
 */

import { DEFAULT_LOCALE, isSupported, type LocaleCode } from './locales'

export interface DetectInput {
  /** User's explicit choice from settings. Always wins if present. */
  explicit?: string | null
  /** `navigator.languages` — array of BCP 47 tags, ordered by preference. */
  navigatorLanguages?: readonly string[] | null
  /** From `chrome.i18n.getUILanguage()`. */
  extensionUiLanguage?: string | null
  /** ISO 3166-1 alpha-2, e.g. 'IL', 'FR'. From PostHog / Cloudflare / Stripe. */
  geoipCountry?: string | null
  /** `Intl.DateTimeFormat().resolvedOptions().timeZone` — weak signal. */
  timezone?: string | null
}

/**
 * Map country codes → locale. Ordered so that the most populous diaspora
 * mapping wins. Countries not listed fall through to later signals.
 */
const COUNTRY_TO_LOCALE: Record<string, LocaleCode> = {
  IL: 'he',
  FR: 'fr', BE: 'fr', CH: 'fr', LU: 'fr', MC: 'fr',
  RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru', UA: 'ru', // Ukraine is complex but Russian is widely understood
  ES: 'es', AR: 'es', MX: 'es', CL: 'es', PE: 'es', CO: 'es', UY: 'es', VE: 'es',
  BR: 'pt', PT: 'pt',
  DE: 'de', AT: 'de',
}

/**
 * Map timezones → locale. Only high-confidence ones (avoids over-matching).
 */
const TIMEZONE_TO_LOCALE: Record<string, LocaleCode> = {
  'Asia/Jerusalem':       'he',
  'Asia/Tel_Aviv':        'he',
  'Europe/Paris':         'fr',
  'Europe/Moscow':        'ru',
  'Europe/Madrid':        'es',
  'America/Argentina/Buenos_Aires': 'es',
  'America/Mexico_City':  'es',
  'America/Sao_Paulo':    'pt',
  'Europe/Berlin':        'de',
}

/** Normalize a BCP 47 tag like 'fr-CA', 'he-IL', 'pt-br' to a base code. */
function languageTag(tag: string | null | undefined): string | null {
  if (!tag) return null
  const base = tag.toLowerCase().split(/[-_]/)[0]
  return base || null
}

/**
 * Resolve the best locale match from available signals. Pure — safe to
 * memoize. Call from both the web app and the extension.
 */
export function detectLocale(input: DetectInput = {}): LocaleCode {
  // 1. Explicit wins, always.
  if (isSupported(input.explicit)) return input.explicit

  // 2. navigator.languages (strongest passive signal).
  const navList = input.navigatorLanguages || []
  for (const tag of navList) {
    const base = languageTag(tag)
    if (isSupported(base)) return base
  }

  // 3. Extension UI language (if we're in the extension).
  const extBase = languageTag(input.extensionUiLanguage)
  if (isSupported(extBase)) return extBase

  // 4. GeoIP country code.
  if (input.geoipCountry) {
    const cc = input.geoipCountry.toUpperCase()
    const byCountry = COUNTRY_TO_LOCALE[cc]
    if (byCountry) return byCountry
  }

  // 5. Timezone (weak but catches diaspora without geoip).
  if (input.timezone) {
    const byTz = TIMEZONE_TO_LOCALE[input.timezone]
    if (byTz) return byTz
  }

  return DEFAULT_LOCALE
}

/**
 * Collect the browser-available signals without reading storage.
 * The caller adds `explicit` and `geoipCountry` on top.
 */
export function browserSignals(): Omit<DetectInput, 'explicit' | 'geoipCountry'> {
  if (typeof window === 'undefined') return {}
  return {
    navigatorLanguages:
      (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || null,
    extensionUiLanguage:
      typeof chrome !== 'undefined' && chrome?.i18n?.getUILanguage
        ? chrome.i18n.getUILanguage()
        : null,
    timezone:
      typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || null
        : null,
  }
}

// chrome types — declared loosely so this file compiles without DOM ambient.
declare global {
  // eslint-disable-next-line no-var
  var chrome: { i18n?: { getUILanguage?: () => string } } | undefined
}
